import { FilterAlt } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { FilterBuilder } from "modules/record/record-producer/components/record-producer-fields/condition-field/FilterBuilder";
import { LogicalNode } from "modules/record/record-producer/types/query.types";

interface FilterEditorProps {
    value: LogicalNode | string | null | undefined;
    onChange: (value: any) => void;
    columns: SysColumn[];
    allColumns: SysColumn[];
    tables: SysTable[];
    formFields?: { name: string; label: string }[];
    buttonLabel: string;
    title: string;
}

const hasCondition = (value: LogicalNode | string | null | undefined): boolean => {
    if (!value) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "object" && Array.isArray((value as LogicalNode).conditions)) {
        return (value as LogicalNode).conditions.length > 0;
    }
    return false;
};

const FilterEditor = ({ value, onChange, columns, allColumns, tables, formFields, buttonLabel, title }: FilterEditorProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<LogicalNode | string | null | undefined>(value ?? null);

    useEffect(() => {
        if (!open) return;
        setDraft(value ?? null);
    }, [open, value]);

    const save = () => {
        onChange(draft);
        setOpen(false);
    };

    return (
        <Box>
            <Button size="small" variant="outlined" startIcon={<FilterAlt />} onClick={() => setOpen(true)} fullWidth>
                {buttonLabel} {hasCondition(value) ? t("service_portal.form_builder.configured") : ""}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <FilterBuilder
                            value={draft}
                            columns={columns}
                            allColumns={allColumns}
                            tables={tables}
                            formFields={formFields}
                            onChange={setDraft}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                    <Button variant="contained" onClick={save}>{t("Apply")}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FilterEditor;
